﻿import * as maplibregl from 'maplibre-gl';
import { MapOptions } from './Models/MapOptions';

// Capture the library script's location before asynchronous map initialization.
const componentsScriptUrl = document.currentScript?.getAttribute('src');
const componentsAssetsUrl = new URL('../', new URL(componentsScriptUrl
    ?? '_content/Gac.Components.Blazor/js/main.js', document.baseURI));

/**
 * Represents a vector map.
 */
export class VectorMap {
    private map: maplibregl.Map;
    private dotNetRef: any;
    private locationMarkers: maplibregl.Marker[] = [];
    private selectedTripId: string | null = null;
    private clickHandledByLayer = false;

    private static readonly layers = {
        line: 'line-route',
        point: 'point-route',
        pointDimmed: 'point-route-dimmed',
    } as const;

    /**
     * Initializes the vector map.
     * @param options - The map options.
     * @param routes - The GeoJSON data.
     * @param markers - The GeoJSON data.
     * @param dotNetReference - The .NET reference.
     * @returns The initialized map.
     */
    constructor(options: MapOptions, routes: GeoJSON.GeoJSON, markers: GeoJSON.GeoJSON, dotNetReference: any) {
        this.map = this.InitMap(options, routes, markers);
        this.dotNetRef = dotNetReference;
    }

    /**
     * Initializes the vector map.
     * @param options - The map options.
     * @param geoJson - The GeoJSON data.
     * @returns The initialized map.
     */
    public InitMap(options: MapOptions, routes: GeoJSON.GeoJSON, markers: GeoJSON.GeoJSON): maplibregl.Map {
        if (maplibregl.getRTLTextPluginStatus() === 'unavailable') {
            maplibregl.setRTLTextPlugin(
                'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
                true
            );
        }

        const mapContainer = document.getElementById('map');
        if (!mapContainer) {
            console.warn(`Container 'map' not found.`);
            return;
        }

        const mapLocation = new maplibregl.LngLat(options.longitude, options.latitude);
        const map = new maplibregl.Map({
            container: mapContainer,
            zoom: options.zoom,
            pitch: options.pitch,
            minZoom: options.minZoom,
            center: mapLocation,
            hash: true,
            style: 'https://vectormaps-resources.myptv.com/styles/latest/standard.json',
            canvasContextAttributes: {
                antialias: true,
            },
            transformRequest: (url, resourceType) => {
                if (resourceType === 'Tile' && url.startsWith('https://api.myptv.com')) {
                    return {
                        url: url + '?apiKey=' + options.apiKey
                    }
                }
            }
        });

        map.on('load', async () => {
            const icons = [
                { name: 'stop-marker-baby', url: '/_content/Gac.Components.Blazor/img/maps/stop-marker-baby.png' },
                { name: 'stop-marker-blue', url: '/_content/Gac.Components.Blazor/img/maps/stop-marker-blue.png' },
                { name: 'stop-marker-gray', url: '/_content/Gac.Components.Blazor/img/maps/stop-marker-gray.png' },
                { name: 'stop-marker-green', url: '/_content/Gac.Components.Blazor/img/maps/stop-marker-green.png' },
                { name: 'stop-marker-marine', url: '/_content/Gac.Components.Blazor/img/maps/stop-marker-marine.png' },
                { name: 'stop-marker-midnight', url: '/_content/Gac.Components.Blazor/img/maps/stop-marker-midnight.png' },
                { name: 'stop-marker-orange', url: '/_content/Gac.Components.Blazor/img/maps/stop-marker-orange.png' },
                { name: 'stop-marker-red', url: '/_content/Gac.Components.Blazor/img/maps/stop-marker-red.png' },
                { name: 'stop-marker-yellow', url: '/_content/Gac.Components.Blazor/img/maps/stop-marker-yellow.png' },
            ];

            for (const icon of icons) {
                const image = await map.loadImage(icon.url);
                map.addImage(icon.name, image.data, { sdf: true });
            }

            map.addSource('routes', {
                type: 'geojson',
                data: routes
            });

            map.addLayer({
                id: VectorMap.layers.line,
                type: 'line',
                source: 'routes',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': ['get', 'ColorCode'],
                    'line-width': 8
                }
            });

            map.addLayer({
                id: VectorMap.layers.pointDimmed,
                type: 'symbol',
                source: 'routes',
                layout: {
                    'icon-image': 'stop-marker-{ColorName}',
                    "icon-size": [
                        'interpolate', ['linear'], ['zoom'],
                        1, 0.01,
                        15, 1
                    ],
                    "icon-allow-overlap": true,
                    "icon-ignore-placement": true,
                    'text-field': '{StopName}',
                    "text-font": [
                        "Noto Sans Regular"
                    ],
                    "text-size": [
                        'interpolate', ['linear'], ['zoom'],
                        1, 4,
                        15, 16
                    ],
                    'text-allow-overlap': true,
                    'text-ignore-placement': true,
                },
                paint: {
                    "icon-color": ['get', 'LightColorCode'],
                    "text-color": "rgba(255,255,255,0.5)",
                },
                filter: ['literal', false]
            });

            map.addLayer({
                id: VectorMap.layers.point,
                type: 'symbol',
                source: 'routes',
                layout: {
                    'icon-image': 'stop-marker-{ColorName}',
                    "icon-size": [
                        'interpolate', ['linear'], ['zoom'],
                        1, 0.01,
                        15, 1
                    ],
                    "icon-allow-overlap": true,
                    "icon-ignore-placement": true,
                    'text-field': '{StopName}',
                    "text-font": [
                        "Noto Sans Regular"
                    ],
                    "text-size": [
                        'interpolate', ['linear'], ['zoom'],
                        1, 4,
                        15, 16
                    ],
                    'text-allow-overlap': true,
                    'text-ignore-placement': true,
                },
                paint: {
                    "icon-color": ['get', 'ColorCode'],
                    "text-color": "#fff",
                },
                filter: ['==', '$type', 'Point']
            });

            map.on('mouseenter', VectorMap.layers.line, (e) => {
                const tripId = this.getTripId(e);
                if (tripId == null) return;

                map.getCanvas().style.cursor = 'pointer';

                if (this.selectedTripId !== null) return;

                this.applyHoverState(tripId);
            });

            map.on('mouseleave', VectorMap.layers.line, () => {
                map.getCanvas().style.cursor = '';
                if (this.selectedTripId === null) {
                    this.applyDefaultState();
                }
            });

            map.on('click', VectorMap.layers.line, (e) => {
                const tripId = this.getTripId(e);
                if (tripId == null) return;

                this.clickHandledByLayer = true;
                this.selectedTripId = tripId;
                this.applySelectionState(tripId);
            });

            map.on('mouseenter', VectorMap.layers.point, () => {
                map.getCanvas().style.cursor = 'pointer';
            });

            map.on('mouseleave', VectorMap.layers.point, () => {
                map.getCanvas().style.cursor = '';
            });

            map.on('click', VectorMap.layers.point, (e) => {
                if (e.features == null || e.features.length === 0) return;

                const tripId = this.getTripId(e);
                if (tripId != null) {
                    this.clickHandledByLayer = true;
                    this.selectedTripId = tripId;
                    this.applySelectionState(tripId);
                }
                this.dotNetRef.invokeMethodAsync('OnStopClicked', e.features[0].properties.StopId);
            });

            map.on('click', () => {
                if (this.clickHandledByLayer) {
                    this.clickHandledByLayer = false;
                    return;
                }
                if (this.selectedTripId !== null) {
                    this.selectedTripId = null;
                    this.applyDefaultState();
                }
            });

            map.getCanvas().addEventListener('mouseleave', () => {
                map.getCanvas().style.cursor = '';
                if (this.selectedTripId === null) {
                    this.applyDefaultState();
                }
            });

            this.updateLocationMarkers(markers);
        });

        if (options.showNavigationControl === true) {
            map.addControl(new maplibregl.NavigationControl());
        }

        if (options.showGeolocationControl === true) {
            map.addControl(new maplibregl.GeolocateControl({
                positionOptions: { enableHighAccuracy: true },
                trackUserLocation: true
            }));
        }

        return map;
    }

    /**
     * Updates the GeoJSON data of the specified source.
     * @param sourceName - The name of the source.
     * @param geoJson - The updated GeoJSON data.
     */
    public UpdateGeoData(sourceName: string, geoJson: GeoJSON.GeoJSON) {
        if (this.map === undefined) {
            return;
        }

        if (sourceName === 'markers') {
            this.updateLocationMarkers(geoJson);
            return;
        }

        var source = this.map.getSource(sourceName);
        if (source) {
            (source as maplibregl.GeoJSONSource).setData(geoJson);
        } else {
            console.error(`Source '${sourceName}' does not exist.`);
        }
    }

    /**
     * <summary>
     * Creates a MapLibre expression that applies a conditional value based on a matching TripId.
     * </summary>
     * <param name="tripId">The TripId to match against feature properties.</param>
     * <param name="matched">The value to return when the TripId matches.</param>
     * <param name="fallback">The value to return when the TripId does not match.</param>
     * <returns>A MapLibre "case" expression.</returns>
     */
    private static tripCase(tripId: string, matched: any, fallback: any): any {
        return ['case', ['==', ['to-string', ['get', 'TripId']], tripId], matched, fallback];
    }

    /**
     * <summary>
     * Creates a filter expression that selects point features matching the specified TripId.
     * </summary>
     * <param name="tripId">The TripId to match.</param>
     * <returns>A MapLibre filter expression for matching point features.</returns>
     */
    private static pointsMatchingTrip(tripId: string): any {
        return ['all', ['==', '$type', 'Point'], ['==', 'TripId', tripId]];
    }

    /**
     * <summary>
     * Creates a filter expression that selects point features excluding the specified TripId.
     * </summary>
     * <param name="tripId">The TripId to exclude.</param>
     * <returns>A MapLibre filter expression for non-matching point features.</returns>
     */
    private static pointsExcludingTrip(tripId: string): any {
        return ['all', ['==', '$type', 'Point'], ['!=', 'TripId', tripId]];
    }

    /**
     * <summary>
     * Retrieves the TripId from the first feature of a map mouse event.
     * </summary>
     * <param name="e">The MapLibre mouse event containing feature data.</param>
     * <returns>The TripId as a string if available; otherwise, null.</returns>
     */
    private getTripId(e: maplibregl.MapLayerMouseEvent): string | null {
        if (e.features == null || e.features.length === 0) return null;
        const raw = e.features[0].properties?.TripId;
        if (raw == null) return null;
        const tripId = String(raw);
        return tripId.length > 0 ? tripId : null;
    }

    /**
     * Applies a hover visual state to the route line matching the provided TripId.
     * Increases line width and raises render priority for the hovered route.
     *
     * @param tripId - The TripId of the route to highlight on hover.
     * @returns void
     */
    private applyHoverState(tripId: string): void {
        const { line } = VectorMap.layers;
        this.map.setPaintProperty(line, 'line-width', VectorMap.tripCase(tripId, 12, 8));
        this.map.setLayoutProperty(line, 'line-sort-key', VectorMap.tripCase(tripId, 1, 0));
    }

    /**
     * Applies a selected visual state to the route matching the provided TripId.
     * Highlights the selected route, updates styling, and filters related point layers.
     *
     * @param tripId - The TripId of the selected route.
     * @returns void
     */
    private applySelectionState(tripId: string): void {
        const { line, point, pointDimmed } = VectorMap.layers;
        this.map.setPaintProperty(line, 'line-color', VectorMap.tripCase(tripId, ['get', 'ColorCode'], ['get', 'LightColorCode']));
        this.applyHoverState(tripId);
        this.map.setFilter(point, VectorMap.pointsMatchingTrip(tripId));
        this.map.setFilter(pointDimmed, VectorMap.pointsExcludingTrip(tripId));
        this.updateRouteCardHighlight(tripId);
    }

    /**
     * Resets all route and point layers to their default visual state.
     * Clears any selection or hover effects.
     *
     * @returns void
     */
    private applyDefaultState(): void {
        const { line, point, pointDimmed } = VectorMap.layers;
        this.map.setPaintProperty(line, 'line-color', ['get', 'ColorCode']);
        this.map.setPaintProperty(line, 'line-width', 8);
        this.map.setLayoutProperty(line, 'line-sort-key', 0);
        this.map.setFilter(point, ['==', '$type', 'Point']);
        this.map.setFilter(pointDimmed, ['literal', false]);
        this.updateRouteCardHighlight(null);
    }

    /**
     * Clears all native MapLibre markers and recreates them from the supplied GeoJSON.
     * The 'Type' feature property controls the pin colour:
     *   'activity' → green (#388E3C) — used when navigating from "Show location"
     *   anything else → red (#D32F2F) — used for trip location indicators
     */
    private updateLocationMarkers(geoJson: GeoJSON.GeoJSON): void {
        this.locationMarkers.forEach(m => m.remove());
        this.locationMarkers = [];

        const collection = geoJson as GeoJSON.FeatureCollection;
        if (!collection || !collection.features) {
            return;
        }

        for (const feature of collection.features) {
            if (feature.geometry.type !== 'Point') {
                continue;
            }

            const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
            const markerType = feature.properties?.Type ?? '';
            const color = markerType === 'activity' ? '#388E3C' : '#D32F2F';

            const marker = new maplibregl.Marker({ color })
                .setLngLat([lng, lat])
                .addTo(this.map);

            this.locationMarkers.push(marker);
        }
    }

    private updateRouteCardHighlight(tripId: string | null): void {
        document.querySelectorAll<HTMLElement>('[data-trip-id]').forEach(card => {
            card.classList.remove('maps-route-card--highlighted', 'maps-route-card--dimmed');
            if (tripId !== null) {
                if (card.dataset.tripId === String(tripId)) {
                    card.classList.add('maps-route-card--highlighted');
                } else {
                    card.classList.add('maps-route-card--dimmed');
                }
            }
        });
    }
}

window['InitVectorMap'] = async function (options: MapOptions, routes: GeoJSON.GeoJSON, markers: GeoJSON.GeoJSON, dotNetReference: any) {
    const workerUrl = new URL('js/maplibre-gl-worker.mjs', componentsAssetsUrl);
    workerUrl.searchParams.set('v', maplibregl.getVersion());
    maplibregl.setWorkerUrl(workerUrl.href);

    const stylesheetUrl = new URL('css/maplibre-gl.css', componentsAssetsUrl);
    stylesheetUrl.searchParams.set('v', maplibregl.getVersion());
    await window['LoadStylesheet'](stylesheetUrl.href);

    return new VectorMap(options, routes, markers, dotNetReference);
}
